/**
 * QorTrace Labs — sub-page of qortrace.com.
 *
 * Engineering arm of QorTrace. Renders inside the main site shell
 * (global Header + Footer) so the page feels like a section of
 * qortrace.com rather than a separate site. The CONTENT keeps its
 * distinctive dark "research lab" aesthetic — Chivo display headers,
 * JetBrains Mono eyebrows, single brand-green accent, the looping
 * 1080p TLS-handshake terminal MP4 as hero — but every nav action
 * goes through the unified mega-menu.
 *
 * Hub at `/labs`; each service has a long-form detail page at
 * `/labs/services/<slug>`. The subdomain `labs.qortrace.com` is
 * still 301-redirected to `/labs` for legacy share links.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import {
  ArrowRight, Check, ScanSearch, Wrench, ShieldCheck,
  Briefcase, Terminal, Compass, FileSignature, Workflow, Cpu,
  ChevronRight, Shield, Rocket, Calendar,
} from "lucide-react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import ConsultationModal from "../components/ConsultationModal";
import LabsHubHero, { LabsHubHeroCaption } from "../components/labs/LabsHubHero";
import LabsSuiteHeroBand from "../components/labs/LabsSuiteHeroBand";
import LabsTour from "../components/labs/LabsTour";
import usePageMeta from "../hooks/usePageMeta";
import M from "../lib/pageMetaConfig";
import "../components/labs/LabsHubHero.css";
import "./Labs.css";
import "./LabsTour.css";


const API = process.env.REACT_APP_BACKEND_URL;

const ICON_FOR = {
  scan:      ScanSearch,
  wrench:    Wrench,
  shield:    ShieldCheck,
  briefcase: Briefcase,
};


// ─────────────────────────────────────────────────────────────────────── ##
// Tiny scroll-reveal hook — IntersectionObserver toggles `is-in` on
// children so CSS handles the fade/translate. No external lib.
// ─────────────────────────────────────────────────────────────────────── ##
function useReveal() {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    // Use a microtask deferral so the IO is set up after React paints.
    const setup = () => {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (e.isIntersecting) {
              e.target.classList.add("is-in");
              io.unobserve(e.target);
            }
          });
        },
        { threshold: 0.08, rootMargin: "0px 0px -6% 0px" },
      );
      ref.current.querySelectorAll("[data-reveal]").forEach((el) => io.observe(el));
      // Safety net: any data-reveal still hidden after 1.5s gets revealed.
      // Covers cases where IO never fires (e.g. very tall sections offscreen
      // on initial load with prefers-reduced-motion or unusual viewports).
      const safety = setTimeout(() => {
        ref.current?.querySelectorAll("[data-reveal]:not(.is-in)").forEach(
          (el) => el.classList.add("is-in"),
        );
      }, 1500);
      return () => { io.disconnect(); clearTimeout(safety); };
    };
    const cleanup = setup();
    return cleanup;
  }, []);
  return ref;
}


function SEOHead() {
  React.useEffect(() => {
    const meta = [
      ["description", "QorTrace Labs — cryptographic engineering for the post-quantum era. Hybrid TLS migration, PQC inventory, smart-contract harvest-exposure audits, CISO readiness."],
      ["og:title", "QorTrace Labs — Cryptographic Engineering for the Post-Quantum Era"],
      ["og:description", "Senior engineers. Fixed-fee scoping. Signed deliverables. Hybrid TLS, ML-KEM, ML-DSA — production-grade, week one."],
      ["og:type", "website"],
      ["og:url", "https://labs.qortrace.com"],
      ["og:image", `${API}/api/public/labs/hero.png`],
      ["og:video", `${API}/api/public/labs/hero.mp4`],
      ["twitter:card", "summary_large_image"],
    ];
    meta.forEach(([k, v]) => {
      const isOg = k.startsWith("og:") || k.startsWith("twitter:");
      const sel = isOg ? `meta[property="${k}"]` : `meta[name="${k}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (isOg) el.setAttribute("property", k); else el.setAttribute("name", k);
        document.head.appendChild(el);
      }
      el.setAttribute("content", v);
    });
    document.title = "QorTrace Labs — Cryptographic Engineering for the Post-Quantum Era";
  }, []);
  return null;
}


// ─────────────────────────────────────────────────────────────────────── ##
// Cal.com consult-trigger CTA. Single source of truth for every Labs
// "Schedule a discovery call" button — opens the existing
// <ConsultationModal> with a per-CTA `source` for analytics. Keeping it
// here (rather than peppering raw <button>s with onClick handlers across
// Hero/InquiryForm/etc) means the component owns its accessible label,
// data-testid pattern, and visual treatment.
// ─────────────────────────────────────────────────────────────────────── ##
function ScheduleCallButton({ source, children, variant = "primary",
                              testid, onOpen }) {
  const cls = variant === "secondary" ? "labs-btn-secondary" : "labs-btn-primary";
  return (
    <button type="button"
            className={cls}
            data-testid={testid}
            data-cta-source={source}
            onClick={() => onOpen(source)}>
      {children || (<>Schedule discovery call <ArrowRight size={14} /></>)}
    </button>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Hero — terminal MP4 on the left side (full bleed inside a framed viewport),
// editorial copy on the right. The MP4 is decorative; we layer copy in front
// of it on small screens and beside it on large screens.
// ─────────────────────────────────────────────────────────────────────── ##
function Hero({ data, onOpenConsult, onStartTour }) {
  return (
    <section className="labs-hero" data-testid="labs-hero">
      <div className="labs-hero-grid">
        {/* iter147: explicit grid rows so the video card aligns to just
            the H1 + lede + CTA row — its top tracks the word
            "Cryptographic" (top of H1) and its bottom tracks the
            bottom edge of the "See what we ship" button. The eyebrow
            sits in its own row above the card, and the meta strip
            sits in its own row below it. The card stretches edge-to-
            edge of this middle row, so its inner animation fills the
            real visual area rather than leaving blank padding above
            and below. */}
        <div className="labs-hero-eyebrow-row">
          <div className="labs-eyebrow">
            <span className="dot" /> {data?.hero?.eyebrow || "QorTrace · Labs"}
          </div>
        </div>

        <div className="labs-hero-copy">
          <h1 className="labs-h1">
            Cryptographic engineering<br />
            for the&nbsp;
            <span className="accent">post-quantum era.</span>
          </h1>
          <p className="labs-lede">
            The engineering arm of QorTrace. We migrate production TLS,
            KMS, code-signing, and on-chain verification from classical
            ECDHE / RSA / ECDSA to hybrid post-quantum primitives —
            without downtime, with senior engineers on the call from
            day one, and signed deliverables on the way out.
          </p>
          <div className="labs-cta-row">
            <ScheduleCallButton source="labs-hero"
                                testid="labs-cta-primary"
                                onOpen={onOpenConsult} />
            <a href="#services" className="labs-btn-secondary">
              See what we ship <ArrowRight size={13} />
            </a>
          </div>
        </div>

        <div className="labs-hero-meta-row">
          <div className="labs-hero-meta" data-testid="labs-hero-meta">
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Median rollout</div>
              <div className="labs-meta-num">11 <span>weeks</span></div>
            </div>
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Senior-led practice</div>
              <div className="labs-meta-num">4 <span>pillars</span></div>
            </div>
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Compliant deliverables</div>
              <div className="labs-meta-num">CNSA <span>2.0</span></div>
            </div>
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Standards covered</div>
              <div className="labs-meta-num">FIPS <span>203/204</span></div>
            </div>
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Public registry</div>
              <div className="labs-meta-num">Live <span>verifiable</span></div>
            </div>
            <div className="labs-meta-item">
              <div className="labs-meta-lbl">Insights cadence</div>
              <div className="labs-meta-num">Biweekly <span>essays</span></div>
            </div>
          </div>
          {/* iter149: inline single-line link that fires the guided
              tour.  Stays underneath the meta cards so it doesn't
              compete visually with the trust-signal numbers above,
              but rides shotgun with them so the eye finds it as the
              "next thing to do" after scanning the strip. */}
          <button type="button"
                  className="labs-hero-tour-link"
                  data-testid="labs-hero-tour-link"
                  onClick={onStartTour}>
            <span className="dot-mini" /> Skim the methodology
            <span className="labs-hero-tour-link-arrow">→</span>
          </button>
        </div>

        <div className="labs-hero-video">
          <LabsHubHeroCaption />
          <div className="labs-hero-video-glow" aria-hidden="true" />
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Manifesto — editorial slab. Big sentence on left, thesis on right.
// ─────────────────────────────────────────────────────────────────────── ##
function Manifesto() {
  return (
    <section id="manifesto" className="labs-manifesto" data-testid="labs-manifesto">
      <div className="labs-manifesto-grid" data-reveal>
        <div className="labs-manifesto-lhs">
          <div className="labs-section-eyebrow"><span className="bar" /> 01 · MANIFESTO</div>
          <h2 className="labs-manifesto-h">
            The data your customers send today will be the&nbsp;<span className="accent">decryption&nbsp;target</span>&nbsp;of 2034.
          </h2>
          <p className="labs-manifesto-sig">
            <span className="bar" /> The QorTrace Labs Engineering Practice
          </p>
        </div>
        <div className="labs-manifesto-body">
          <p>
            Harvest-now-decrypt-later is not a hypothetical. Adversaries
            with budget and patience are already collecting TLS sessions,
            VPN traffic, and on-chain ECDSA signatures with a single
            assumption: that within a decade, a cryptographically relevant
            quantum computer will turn that archive into plaintext.
          </p>
          <p>
            QorTrace Labs exists to make that assumption irrelevant for
            your stack. We don&rsquo;t sell a product. We sell ten
            consecutive weeks of senior cryptographic engineers — embedded
            with your team, shipping production-grade hybrid TLS, ML-KEM
            key-encapsulation, and ML-DSA signatures, and walking out with
            a signed migration certificate the regulator will accept.
          </p>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Service pillars — 2×2 grid. Bigger, more editorial than the v1 cards.
// ─────────────────────────────────────────────────────────────────────── ##
function ServicePillar({ s, n }) {
  const Icon = ICON_FOR[s.icon] || ScanSearch;
  return (
    <article
      className="labs-pillar"
      data-testid={`labs-service-${s.slug}`}
      data-featured={s.featured ? "true" : "false"}
      data-reveal
    >
      <div className="labs-pillar-head">
        <div className="labs-pillar-num">{String(n).padStart(2, "0")}</div>
        <div className="labs-pillar-icon"><Icon size={18} strokeWidth={1.5} /></div>
        <div className="labs-pillar-cat">{s.category.toUpperCase()}</div>
      </div>
      <h3 className="labs-pillar-title">{s.title}</h3>
      <p className="labs-pillar-tagline">{s.tagline}</p>
      <p className="labs-pillar-desc">{s.description}</p>
      <ul className="labs-pillar-deliv" aria-label="Deliverables">
        {s.deliverables.map((d) => (
          <li key={d}><span className="arr">→</span> {d}</li>
        ))}
      </ul>
      <div className="labs-pillar-foot">
        <span className="labs-pillar-weeks">
          {Array.isArray(s.duration_weeks)
            ? `${s.duration_weeks[0]}–${s.duration_weeks[1]} weeks`
            : "—"}
        </span>
        <Link to={`/labs/services/${s.slug}`} className="labs-pillar-cta">
          Open service <ArrowRight size={13} />
        </Link>
      </div>
    </article>
  );
}

function Services({ services }) {
  return (
    <section id="services" className="labs-section" data-testid="labs-services">
      <div className="labs-section-eyebrow"><span className="bar" /> 02 · SERVICES</div>
      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">What we ship.</h2>
        <p className="labs-section-sub">
          Four senior-engineer-led pillars. Fixed-fee scoping, signed
          deliverables, and a senior on the call from day one — not a
          partner-slide pitch and a junior on Slack.
        </p>
      </div>
      <div className="labs-pillars-grid">
        {services.map((s, i) => <ServicePillar key={s.slug} s={s} n={i + 1} />)}
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Methodology — 4-step process timeline.
// ─────────────────────────────────────────────────────────────────────── ##
const PROCESS_STEPS = [
  {
    n: "01",
    icon: Compass,
    title: "Discover",
    body: "30-minute call with a senior engineer. We map your cryptographic surface — TLS, KMS, code-signing, on-chain — and identify your harvest-now exposure.",
    days: "Day 0",
  },
  {
    n: "02",
    icon: Terminal,
    title: "Scope",
    body: "Fixed-fee scoping memo within five business days. Phased plan, named senior leads, deliverables, milestones, and a hard end-date — signed both sides.",
    days: "Days 1–5",
  },
  {
    n: "03",
    icon: Workflow,
    title: "Engineer",
    body: "Senior engineers embed with your team. Production-grade hybrid TLS, ML-KEM, ML-DSA, KMS migration. PRs into your repos, reviewed by your reviewers.",
    days: "Weeks 2–N",
  },
  {
    n: "04",
    icon: FileSignature,
    title: "Sign",
    body: "Cryptographic Migration Certificate — signed PDF, embeddable SVG, verifiable on the QorTrace public registry. Hand-off doc, runbook, telemetry.",
    days: "Final week",
  },
];

function Methodology() {
  return (
    <section id="process" className="labs-section labs-process"
             data-testid="labs-process">
      <div className="labs-section-eyebrow"><span className="bar" /> 03 · METHODOLOGY</div>
      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">How an engagement runs.</h2>
        <p className="labs-section-sub">
          Four phases. Predictable. Auditable. Built to satisfy your
          procurement, your CISO, and your regulator — without slowing
          your engineers down.
        </p>
      </div>
      <ol className="labs-process-list">
        {PROCESS_STEPS.map((p) => {
          const Icon = p.icon;
          return (
            <li key={p.n} className="labs-process-step" data-reveal>
              <div className="labs-process-rail" aria-hidden="true">
                <div className="labs-process-num">{p.n}</div>
                <div className="labs-process-icon">
                  <Icon size={18} strokeWidth={1.5} />
                </div>
              </div>
              <div className="labs-process-body">
                <div className="labs-process-when">{p.days}</div>
                <h3 className="labs-process-title">{p.title}</h3>
                <p className="labs-process-text">{p.body}</p>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Engagements / case studies (redacted).
// ─────────────────────────────────────────────────────────────────────── ##
function CaseCard({ c }) {
  return (
    <article className="labs-case" data-testid={`labs-case-${c.slug}`} data-reveal>
      <div className="labs-case-cat">{c.category}</div>
      <div className="labs-case-stat">{c.stat}</div>
      <h4 className="labs-case-title">{c.title}</h4>
      <p className="labs-case-summary">{c.summary}</p>
      <div className="labs-case-redact">
        <span className="bar" /> NDA-redacted · client name on request
      </div>
    </article>
  );
}

function Engagements({ cases }) {
  return (
    <section id="engagements" className="labs-section" data-testid="labs-engagements">
      <div className="labs-section-eyebrow"><span className="bar" /> 04 · ENGAGEMENTS</div>
      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">Real engagements. Redacted.</h2>
        <p className="labs-section-sub">
          We sign tight NDAs. The shape of the work is public; the names
          aren&rsquo;t.
        </p>
      </div>
      <div className="labs-cases-grid">
        {cases.map((c) => <CaseCard key={c.slug} c={c} />)}
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Team & credentials.
// ─────────────────────────────────────────────────────────────────────── ##
function TeamCredentials({ items }) {
  return (
    <section className="labs-section labs-team" data-testid="labs-team">
      <div className="labs-section-eyebrow"><span className="bar" /> 05 · WHO YOU&rsquo;LL WORK&nbsp;WITH</div>
      <div className="labs-team-grid">
        <div className="labs-team-copy" data-reveal>
          <h2 className="labs-section-title">Senior engineers. No layers.</h2>
          <p className="labs-section-sub">
            Every engagement is led by an engineer with at least eight
            years of applied cryptography or production TLS experience —
            on the call from day one, in your repos by week two, and on
            the regulator response by month three.
          </p>
          <div className="labs-team-tags">
            <span className="tag"><Cpu size={12} /> NIST FIPS 203 · ML-KEM</span>
            <span className="tag"><Cpu size={12} /> NIST FIPS 204 · ML-DSA</span>
            <span className="tag"><Cpu size={12} /> CNSA 2.0</span>
            <span className="tag"><Cpu size={12} /> X25519 · X448</span>
            <span className="tag"><Cpu size={12} /> oqs-provider</span>
            <span className="tag"><Cpu size={12} /> liboqs</span>
          </div>
        </div>

        <div className="labs-team-creds" data-reveal>
          <div className="labs-creds-title">Credentials</div>
          {(items || []).map((c) => (
            <div key={c} className="labs-cred-row">
              <span className="labs-cred-check"><Check size={12} /></span>
              <span>{c}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Inquiry form (preserved from v1, restyled to match the new aesthetic).
// ─────────────────────────────────────────────────────────────────────── ##
function InquiryForm({ services }) {
  const location = useLocation();

  // Deterministic prefill: read `?prefill=<slug>` from the URL and/or
  // `location.state.prefill` (set by service-detail Link navigation) on
  // FIRST render — before any race against lazy-route hydration. The
  // window CustomEvent stays as a fallback for in-page Scope-this clicks
  // on the hub itself (no route boundary to race).
  const initialSlug = (() => {
    const fromState  = location.state?.prefill;
    const fromQuery  = new URLSearchParams(location.search).get("prefill");
    const candidate  = fromState || fromQuery;
    const valid      = (services || []).some((s) => s.slug === candidate);
    return valid ? candidate : (services?.[0]?.slug || "pqc-cryptographic-inventory");
  })();

  const [form, setForm] = useState({
    name: "", work_email: "", company: "", role: "",
    service_slug: initialSlug,
    headcount: "", timeline: "", message: "", consent_terms: false,
  });
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  // If `services` finishes loading AFTER first render and our prefill
  // was deferred, sync once when the catalog arrives.
  useEffect(() => {
    if (!services?.length) return;
    const fromState  = location.state?.prefill;
    const fromQuery  = new URLSearchParams(location.search).get("prefill");
    const candidate  = fromState || fromQuery;
    if (candidate && services.some((s) => s.slug === candidate)) {
      setForm((f) => (f.service_slug === candidate ? f : { ...f, service_slug: candidate }));
    }
    // Intentionally only re-runs when the services array length changes
    // (catalog arrival). location.state/search are stable per-route.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [services?.length]);

  // Fallback: in-page Scope-this clicks on the hub dispatch this event.
  useEffect(() => {
    const handler = (e) => {
      const slug = e.detail?.slug;
      if (slug) setForm((f) => ({ ...f, service_slug: slug }));
    };
    window.addEventListener("labs-prefill-service", handler);
    return () => window.removeEventListener("labs-prefill-service", handler);
  }, []);

  // iter150: handle "continue from here" from the methodology tour.
  // Sets the message field with the tour-completion topic, scrolls
  // the form into view, and auto-focuses the work-email input so the
  // user's next physical action is to type their address.
  useEffect(() => {
    const handler = (e) => {
      const msg = e.detail?.message || "";
      if (msg) setForm((f) => ({ ...f, message: msg }));
      // Defer the focus to the next tick so React has flushed the
      // setForm update (otherwise the focus can fire while the
      // input is still in its prior value's reconciliation).
      window.setTimeout(() => {
        const el = document.querySelector("[data-testid='labs-inquiry-email']");
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus({ preventScroll: true });
        }
      }, 80);
    };
    window.addEventListener("labs-prefill-inquiry", handler);
    return () => window.removeEventListener("labs-prefill-inquiry", handler);
  }, []);

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setStatus(null);
    try {
      // Channel attribution: forward `?ref=<source>` if present so
      // sales can see exactly which LinkedIn essay / podcast / partner
      // referral generated the inquiry.
      const ref = (new URLSearchParams(location.search).get("ref") || "")
                    .trim().slice(0, 60) || undefined;
      await axios.post(`${API}/api/public/labs/inquiry`, { ...form, ref });
      setStatus({ ok: true,
        text: "Inquiry received. A senior engineer will reach out within one business day." });
      setForm((f) => ({
        ...f,
        name: "", work_email: "", company: "", role: "",
        headcount: "", timeline: "", message: "", consent_terms: false,
      }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setStatus({ ok: false,
        text: typeof detail === "string" ? detail : "Submission failed. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="inquiry" className="labs-section labs-inquiry"
             data-testid="labs-inquiry-section">
      <div className="labs-section-eyebrow"><span className="bar" /> 06 · START A CONVERSATION</div>
      <form onSubmit={submit}
            className="labs-inquiry-wrap"
            data-testid="labs-inquiry-form"
            aria-label="QorTrace Labs inquiry form"
            data-reveal>
        <div className="labs-inquiry-head">
          <h2 className="labs-inquiry-title">
            Tell us about your<br />
            <span className="accent">cryptographic surface.</span>
          </h2>
          <p className="labs-inquiry-sub">
            One business day to a senior engineer. NDAs available on
            request. All engagements scoped, fixed-fee, and led by a
            senior on the call.
          </p>
        </div>
        <div className="labs-form-grid">
          <div className="labs-form-row">
            <label htmlFor="lf-name">Your name</label>
            <input id="lf-name" type="text" required
                   data-testid="labs-inquiry-name"
                   value={form.name} onChange={update("name")} />
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-email">Work email</label>
            <input id="lf-email" type="email" required
                   data-testid="labs-inquiry-email"
                   value={form.work_email} onChange={update("work_email")} />
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-company">Company</label>
            <input id="lf-company" type="text" required
                   data-testid="labs-inquiry-company"
                   value={form.company} onChange={update("company")} />
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-role">Your role</label>
            <input id="lf-role" type="text"
                   placeholder="CISO, Head of Eng, etc."
                   data-testid="labs-inquiry-role"
                   value={form.role} onChange={update("role")} />
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-service">Service of interest</label>
            <select id="lf-service" required
                    data-testid="labs-inquiry-service"
                    value={form.service_slug} onChange={update("service_slug")}>
              {services.map((s) => (
                <option key={s.slug} value={s.slug}>{s.title}</option>
              ))}
            </select>
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-headcount">Engineering headcount</label>
            <select id="lf-headcount"
                    data-testid="labs-inquiry-headcount"
                    value={form.headcount} onChange={update("headcount")}>
              <option value="">— select —</option>
              <option value="1-10">1–10</option>
              <option value="11-50">11–50</option>
              <option value="51-200">51–200</option>
              <option value="201-1000">201–1,000</option>
              <option value="1000+">1,000+</option>
            </select>
          </div>
          <div className="labs-form-row">
            <label htmlFor="lf-timeline">Target start</label>
            <select id="lf-timeline"
                    data-testid="labs-inquiry-timeline"
                    value={form.timeline} onChange={update("timeline")}>
              <option value="">— select —</option>
              <option value="urgent">ASAP / urgent</option>
              <option value="quarter">This quarter</option>
              <option value="half">This half</option>
              <option value="year">This year</option>
              <option value="exploring">Just exploring</option>
            </select>
          </div>
          <div className="labs-form-row full">
            <label htmlFor="lf-msg">What are you trying to solve?</label>
            <textarea id="lf-msg" required minLength={10}
                      placeholder="Brief context — your cryptographic surface, regulatory pressure, target outcome."
                      data-testid="labs-inquiry-message"
                      value={form.message} onChange={update("message")} />
          </div>
          <div className="labs-form-row checkbox full">
            <input id="lf-consent" type="checkbox"
                   data-testid="labs-inquiry-consent"
                   checked={form.consent_terms} onChange={update("consent_terms")} />
            <label htmlFor="lf-consent">
              I agree to be contacted by QorTrace Labs about this inquiry.
              We&rsquo;ll never share your information.
              See our <Link to="/legal/privacy">privacy policy</Link>.
            </label>
          </div>
        </div>
        <div className="labs-form-foot">
          <button type="submit" disabled={busy}
                  className="labs-btn-primary"
                  data-testid="labs-inquiry-submit">
            {busy ? "Sending…" : "Schedule discovery call"} <ArrowRight size={14} />
          </button>
          {status && (
            <div className={`labs-form-status ${status.ok ? "ok" : "error"}`}
                 data-testid="labs-inquiry-status">
              {status.text}
            </div>
          )}
        </div>
      </form>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Team profiles — 4 named senior engineers. Initials only on the badge
// (no headshots — most engineers prefer it that way and we honour the
// preference). Each card: initials, role, 2-sentence bio, areas of
// depth. Together with the credentials block it gives the hub the
// "who you'd actually be talking to" answer.
// ─────────────────────────────────────────────────────────────────────── ##
const TEAM_PROFILES = [
  { id: "james-stephens", name: "James Stephens, CBE, CCFI",
    role: "Principal Architect of QorTrace",
    tags: ["Architecture", "Blockchain forensics", "PQC strategy", "Custody"],
    bio:  "James is the Principal Architect of QorTrace, a post-quantum cybersecurity and cryptographic risk intelligence company focused on securing digital assets, blockchain ecosystems, DeFi protocols, custodians, institutions, and individual investors against emerging quantum-era threats. As a Certified Blockchain Expert and Certified Cryptocurrency Forensic Investigator, James brings deep expertise in blockchain infrastructure, digital asset security, crypto forensics, and post-quantum risk strategy. His work centers on identifying hidden cryptographic exposure, strengthening security readiness, and helping organizations prepare for the next generation of cyber risk \u2014 advancing a mission to trace the risk and secure the future.",
  },
  { id: "james-r",  name: "James R.",  role: "Senior \u00b7 Principal Cryptographer",
    tags: ["FIPS 203/204", "EVM", "TLS 1.3", "HSM"],
    bio:  "Twelve years applied cryptography across central-bank settlement, digital-asset custody, and Layer-1 chain design. Lead author of the QorTrace migration methodology and the public-registry certificate spec." },
  { id: "raj-p",    name: "Raj P.",    role: "Senior \u00b7 Quantum-Safe Networking",
    tags: ["TLS 1.3", "ML-KEM hybrids", "QUIC", "Service mesh"],
    bio:  "Network-layer cryptographer who has shipped hybrid X25519+ML-KEM on production load balancers, service meshes, and edge proxies. Specialises in zero-downtime cutovers and post-quantum interop across mixed-vendor fleets." },
  { id: "devon-a",  name: "Devon A.",  role: "Senior \u00b7 Compliance & Regulator Liaison",
    tags: ["NIST SP 800-208", "CNSA 2.0", "DORA", "MAS TRM"],
    bio:  "Twenty years across federal cybersecurity policy and financial-services compliance. Translates regulator expectations into engineering acceptance criteria and represents client cryptographic posture in supervisor reviews." },
  { id: "anya-k",   name: "Anya K.",   role: "Senior \u00b7 Lattice Cryptography",
    tags: ["ML-KEM", "ML-DSA", "Side-channel", "Constant-time"],
    bio:  "Lattice researcher turned production engineer. Authored the constant-time review checklist every QorTrace ML-KEM build runs through and has contributed upstream side-channel fixes to liboqs." },
  { id: "marcus-l", name: "Marcus L.", role: "Senior \u00b7 Hybrid TLS Engineering",
    tags: ["X25519+ML-KEM", "OpenSSL 3", "Akamai", "Envoy"],
    bio:  "Shipped hybrid TLS in production at two Tier-1 CDNs and a top-3 global fintech. Co-author of the oqs-provider integration patches we still upstream." },
  { id: "priya-s",  name: "Priya S.",  role: "Senior \u00b7 HSM &amp; KMS Migration",
    tags: ["AWS KMS", "Thales", "Entrust", "ML-DSA-65"],
    bio:  "AWS, GCP, Azure, and on-premise HSM migrations across financial-services and healthcare. Author of the KMS-rotation runbook every QorTrace engagement ships against." },
  { id: "luca-b",   name: "Luca B.",   role: "Senior \u00b7 Smart-Contract Audit",
    tags: ["Solidity", "ERC-4337", "Bridge", "Foundry"],
    bio:  "Solidity auditor with eight years on bridges, AMMs, and L2 rollups. Wrote the harvest-exposure scoring model that powers QorTrace\u2019s smart-contract PQC audit deliverable." },
  { id: "sara-m",   name: "Sara M.",   role: "Senior \u00b7 Migration Architecture",
    tags: ["GCP KMS", "Sigstore", "Cosign", "Supply chain"],
    bio:  "Designed the GCP\u2192PQC migration blueprint for a top-five payments processor. Specialises in zero-downtime rollouts and supply-chain attestation across multi-cloud KMS surfaces." },
  { id: "kenji-t",  name: "Kenji T.",  role: "Senior \u00b7 Code-Signing Pipelines",
    tags: ["Sigstore", "GitHub OIDC", "ML-DSA", "SLH-DSA"],
    bio:  "Built the dual-family (ML-DSA + SLH-DSA) code-signing pipeline that ships on Methodology v1.5. Veteran of two CI/CD security teams at consumer-OS vendors." },
  { id: "nadia-h",  name: "Nadia H.",  role: "Senior \u00b7 Regulatory &amp; Compliance",
    tags: ["FIPS 140-3", "CNSA 2.0", "DORA", "EO 14028"],
    bio:  "Former regulator-side reviewer. Maps every QorTrace engagement to the specific FIPS, CNSA, DORA, and EO 14028 clauses your auditor is going to ask about." },
  { id: "owen-p",   name: "Owen P.",   role: "Senior \u00b7 On-Chain Signatures",
    tags: ["ECDSA-secp256k", "EVM", "Solana", "ZK"],
    bio:  "Ten years of on-chain ECDSA forensics across exchanges and bridges. Quantifies harvest-exposure of dormant signed transactions before they become quantum liabilities." },
  { id: "zara-w",   name: "Zara W.",   role: "Senior \u00b7 Bridge &amp; Cross-Chain",
    tags: ["LayerZero", "Wormhole", "Axelar", "ZK"],
    bio:  "Audited bridges holding eleven-figure TVL. Knows where ECDSA-only relayers sit in your message-passing flow and how to phase ML-DSA in without breaking liveness." },
  { id: "ravi-c",   name: "Ravi C.",   role: "Senior \u00b7 TLS &amp; Network Crypto",
    tags: ["TLS 1.3", "QUIC", "BoringSSL", "Cloudflare"],
    bio:  "Worked on TLS at hyperscale before joining QorTrace. Owns the hybrid-handshake telemetry pipeline we use to prove a migration actually negotiated PQC end-to-end." },
  { id: "elena-v",  name: "Elena V.",  role: "Senior \u00b7 FIPS-Validated Modules",
    tags: ["FIPS 140-3", "Module review", "ML-KEM", "Audit"],
    bio:  "Eleven years inside a FIPS-validating lab. Reviews QorTrace builds for module-boundary compliance before they go anywhere near a CMVP submission." },
  { id: "darius-o", name: "Darius O.", role: "Senior \u00b7 Supply-Chain Attestation",
    tags: ["SLSA", "in-toto", "Provenance", "SBOM"],
    bio:  "Designed SLSA-level-4 attestation flows for two enterprise SaaS vendors. Built the in-toto layout QorTrace clients use to prove the migration came from us, unmodified." },
  { id: "mei-l",    name: "Mei L.",    role: "Senior \u00b7 L1 Protocol Engineering",
    tags: ["Move", "Aptos", "Sui", "Consensus"],
    bio:  "Protocol engineer who shipped consensus changes on two non-EVM L1s. Maps PQC migration paths for chains where the signature scheme is baked into the runtime." },
  { id: "thomas-k", name: "Thomas K.", role: "Senior \u00b7 Banking Cryptography",
    tags: ["HSM", "EMV", "ISO 8583", "Mainframe"],
    bio:  "Thirty years of payment-grade cryptography across European Tier-1 banks. The engineer your CISO\u2019s CISO calls when the migration touches a mainframe HSM." },
  { id: "fatima-d", name: "Fatima D.", role: "Senior \u00b7 Fintech Regulatory",
    tags: ["PSD2", "DORA", "MiCA", "Audit-ready"],
    bio:  "Bridges engineering and regulatory translation. Co-author of the EU regulatory-clause mapping every QorTrace Labs fintech engagement carries on its certificate." },
];

function TeamProfiles() {
  return (
    <section className="labs-section labs-team-profiles-section"
             data-testid="labs-team-profiles">
      <div className="labs-section-eyebrow">
        <span className="bar" /> 06 &middot; WHO YOU&rsquo;LL TALK TO
      </div>
      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">Senior engineers.</h2>
        <p className="labs-section-sub">
          A working bench of {TEAM_PROFILES.length}. One will be on your
          discovery call &mdash; matched by stack, sector, and timezone.
          No partner-slide intermediaries, no handover to a junior on
          Slack. Names are first-name and last-initial only because
          client engagements are NDA-protected and we don&rsquo;t dox
          our engineers.
        </p>
      </div>
      <div className="labs-team-profiles-rail"
           data-testid="labs-team-profiles-rail">
        {TEAM_PROFILES.map((p) => (
          <article key={p.id}
                   className="labs-profile"
                   data-testid={`labs-profile-${p.id}`}>
            <div className="labs-profile-avatar">
              <img src={`/labs/team/${p.id}.jpg`}
                   alt=""
                   loading="lazy"
                   width="120" height="120"
                   onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
            <h3 className="labs-profile-name">{p.name}</h3>
            <div className="labs-profile-role"
                 dangerouslySetInnerHTML={{ __html: p.role }} />
            <p className="labs-profile-bio">{p.bio}</p>
            <div className="labs-profile-tags">
              {p.tags.map((t) => (
                <span key={t} className="labs-profile-tag">{t}</span>
              ))}
            </div>
          </article>
        ))}
      </div>
      <div className="labs-team-profiles-hint" aria-hidden="true">
        &larr; scroll &rarr;
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Hub-level FAQ — answers the "is this for me?" questions buyers ask
// BEFORE they click into a service detail page. Service-specific FAQs
// stay on the per-service detail pages.
// ─────────────────────────────────────────────────────────────────────── ##
const HUB_FAQS = [
  { q: "How is QorTrace Labs different from my existing PQC consultancy?",
    a: "We do not pitch a 200-slide deck and hand off to a junior. Every engagement is led by one of the senior engineers above, fixed-fee scoped within five business days, and ends with a registry-verifiable certificate — not a recommendation deck." },
  { q: "Do you sign mutual NDAs?",
    a: "Yes — standard. Most clients have an MNDA we can countersign before the discovery call. We default to redacting client names from public case studies and release them 1:1 to qualified prospects under NDA." },
  { q: "What is the smallest engagement you take?",
    a: "Two-day CISO PQC Readiness Workshop is the smallest. Cryptographic Inventory ships in 4–8 weeks. Full PQC Migration Engineering averages 11 weeks. We do not do half-day reviews." },
  { q: "Do you sub-contract or staff augment?",
    a: "No. Every engineer billed on your engagement is a QorTrace Labs engineer. We do not white-label external talent." },
  { q: "What stacks have you shipped this on?",
    a: "Cloudflare, Fastly, Akamai, AWS CloudFront, NGINX, HAProxy, Envoy, AWS / GCP / Azure KMS, Thales, Entrust, plus EVM L1/L2, Solana, Aptos, Sui. Anything else, we extend." },
  { q: "Can you start under an existing MSA with QorTrace?",
    a: "Yes. Labs engagements can be SOW'd against your existing QorTrace MSA — your procurement team only sees one supplier on file." },
];

function HubFAQ() {
  return (
    <section className="labs-section labs-hub-faq" data-testid="labs-hub-faq">
      <div className="labs-section-eyebrow"><span className="bar" /> 07 · BEFORE YOU CLICK INTO A SERVICE</div>
      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">Frequently asked.</h2>
        <p className="labs-section-sub">
          Service-specific FAQs sit on each service&rsquo;s detail
          page — these are the cross-cutting questions buyers ask
          before they pick a pillar.
        </p>
      </div>
      <div className="labs-faq-list" data-reveal>
        {HUB_FAQS.map((f, i) => (
          <details key={f.q} className="labs-faq-item"
                   data-testid={`labs-hub-faq-${i}`}>
            <summary>
              <span className="labs-faq-q">{f.q}</span>
              <ChevronRight size={16} className="labs-faq-chev" />
            </summary>
            <div className="labs-faq-a">{f.a}</div>
          </details>
        ))}
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Roadmap teaser — what's shipping next from the Labs practice.
//
// Iter235 — Data is now sourced live from
// `GET /api/public/labs/roadmap` (Mongo-backed, admin-editable so we
// can re-issue the public roadmap on the 90-day methodology cadence
// without a re-deploy). Falls back to the previously-shipped
// hardcoded list if the endpoint is unreachable so the section
// never goes blank.
//
// Horizons render distinct pills:
//   • now     → cyan  "SHIPPING NOW"
//   • soon    → cyan  "NEXT 90 DAYS"
//   • later   → muted "LATER 2026"
//   • shipped → GREEN "✓ SHIPPED"   (auto-hidden after 90 days
//                                     by the public endpoint)
// ─────────────────────────────────────────────────────────────────────── ##
const ROADMAP_FALLBACK = [
  { id: "fb-1", when: "now",   title: "Cryptographic Migration Certificate v1.5",
    body: "Live on every active engagement. v1.5 adds a FIPS 205 (SLH-DSA-SHA2-128s) co-signature beside the ML-DSA-65 primary so an algorithmic break against either family alone cannot forge an attestation.",
    target: "" },
  { id: "fb-2", when: "soon",  title: "Hardware-token attestation",
    body: "FIDO2 / WebAuthn attestation for engagement signers.",
    target: "Q3 2026" },
  { id: "fb-3", when: "soon",  title: "Repo-OAuth Inventory v2",
    body: "Direct GitLab Self-Managed and Bitbucket Server connectors.",
    target: "Q2 2026" },
  { id: "fb-4", when: "later", title: "Continuous Cryptographic Monitoring",
    body: "Standing telemetry feed — classical-fallback alerts after the migration ships.",
    target: "Late 2026" },
];

const ROADMAP_PILL = {
  now:     "SHIPPING NOW",
  soon:    "NEXT 90 DAYS",
  later:   "LATER",
  shipped: "✓ SHIPPED",
};

function RoadmapTeaser() {
  const [items, setItems] = useState(ROADMAP_FALLBACK);
  const [asOf,  setAsOf]  = useState(null);
  const [latestShipped, setLatestShipped] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/api/public/labs/roadmap`)
      .then((r) => {
        if (cancelled) return;
        const live = r?.data?.items || [];
        if (live.length) {
          setItems(live);
          if (r?.data?.as_of) setAsOf(r.data.as_of);
        }
        // Iter242 — surface the "freshly shipped" banner only when
        // backend explicitly returned a sub-7-day-old shipped item.
        if (r?.data?.latest_shipped) {
          setLatestShipped(r.data.latest_shipped);
        }
      })
      .catch(() => {
        // Silent — keep fallback so the section never goes blank.
      });
    return () => { cancelled = true; };
  }, []);

  // Format "Updated 2h ago" style timestamp so visitors can see this
  // section is live, not a stale brochure plant. Recomputes only on
  // mount — exact freshness isn't worth the re-render churn.
  const asOfPretty = useMemo(() => {
    if (!asOf) return "";
    try {
      const t = new Date(asOf).getTime();
      const dt = Date.now() - t;
      const mins = Math.max(0, Math.round(dt / 60_000));
      if (mins < 1)   return "Updated just now";
      if (mins < 60)  return `Updated ${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24)   return `Updated ${hrs}h ago`;
      const days = Math.round(hrs / 24);
      return `Updated ${days}d ago`;
    } catch {
      return "";
    }
  }, [asOf]);

  // Iter242 — "X days ago" pretty timestamp for the latest-shipped
  // banner. Mirrors `asOfPretty` but with "shipped" semantics.
  const shippedAgo = useMemo(() => {
    if (!latestShipped?.shipped_at) return "";
    try {
      const t  = new Date(latestShipped.shipped_at).getTime();
      const dt = Date.now() - t;
      const hrs = Math.max(0, Math.round(dt / 3_600_000));
      if (hrs < 1)  return "just now";
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      if (days === 1) return "yesterday";
      return `${days} days ago`;
    } catch {
      return "";
    }
  }, [latestShipped]);

  return (
    <section className="labs-section" data-testid="labs-roadmap">
      <div className="labs-section-eyebrow">
        <span className="bar" />
        <Rocket size={11} style={{ marginRight: 4 }} /> 08 · COMING NEXT
      </div>

      {latestShipped && shippedAgo ? (
        <div
          data-testid="labs-roadmap-fresh-ship-banner"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 22,
            padding: "10px 16px",
            border: "1px solid rgba(31,221,131,0.40)",
            borderRadius: 999,
            background:
              "linear-gradient(90deg, rgba(31,221,131,0.10), rgba(31,221,131,0.02))",
            maxWidth: "fit-content",
            boxShadow: "0 0 0 1px rgba(31,221,131,0.06)",
          }}
        >
          <span
            aria-hidden="true"
            style={{ fontSize: 16, lineHeight: 1, marginTop: 1 }}
          >
            🚢
          </span>
          <span
            className="qt-mono"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.16em",
              color: "var(--labs-green, #1FDD83)",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            JUST SHIPPED
          </span>
          <span
            style={{
              width: 1, height: 14,
              background: "rgba(31,221,131,0.35)",
            }}
            aria-hidden="true"
          />
          <span
            data-testid="labs-roadmap-fresh-ship-title"
            style={{ color: "#fff", fontSize: 13.5, fontWeight: 500 }}
          >
            {latestShipped.title}
          </span>
          <span
            className="qt-mono"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.10em",
              color: "var(--labs-fg-faint, #7A8AA1)",
              textTransform: "uppercase",
            }}
          >
            · {shippedAgo}
          </span>
          <a
            href="/labs/wall-of-shipped"
            data-testid="labs-roadmap-wall-link"
            className="qt-mono"
            style={{
              fontSize: 10.5,
              letterSpacing: "0.10em",
              color: "var(--labs-green, #1FDD83)",
              textDecoration: "none",
              marginLeft: 6,
              borderBottom: "1px dashed rgba(31,221,131,0.5)",
              paddingBottom: 1,
            }}
          >
            VIEW THE WALL →
          </a>
        </div>
      ) : null}

      <div className="labs-section-head" data-reveal>
        <h2 className="labs-section-title">What we&rsquo;re shipping next.</h2>
        <p className="labs-section-sub">
          The QorTrace Labs methodology is versioned and re-issued on a
          rolling 90-day cadence. Below is the current public roadmap.
        </p>
        {asOfPretty ? (
          <div
            className="qt-mono"
            data-testid="labs-roadmap-asof"
            style={{
              marginTop: 12,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "var(--qt-text-3, #7A8AA1)",
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 6, height: 6, borderRadius: "50%",
                background: "#1FDD83",
                boxShadow: "0 0 8px rgba(31,221,131,0.55)",
                animation: "qt-live-pulse 1.6s ease-in-out infinite",
              }}
            />
            LIVE · {asOfPretty.toUpperCase()}
          </div>
        ) : null}
      </div>
      {items.length === 0 ? (
        <div
          className="qt-mono"
          data-testid="labs-roadmap-empty"
          style={{
            marginTop: 24,
            padding: "20px",
            border: "1px dashed var(--qt-border, #1E2B45)",
            borderRadius: 12,
            color: "var(--qt-text-3, #7A8AA1)",
            fontSize: 12,
            letterSpacing: "0.08em",
            textAlign: "center",
          }}
        >
          Roadmap refresh in progress — check back shortly.
        </div>
      ) : (
        <div
          className="labs-roadmap-grid labs-roadmap-grid-scroll"
          data-testid="labs-roadmap-grid"
        >
          {items.map((r) => {
            const pill = ROADMAP_PILL[r.when] || r.when.toUpperCase();
            return (
              <article
                key={r.id || r.slug || r.title}
                className="labs-roadmap-card"
                data-when={r.when}
                data-testid={`labs-roadmap-${r.when}`}
                data-slug={r.slug || ""}
                data-reveal
              >
                <div className="labs-roadmap-when">
                  <span className="dot" />
                  {pill}
                </div>
                <h3 className="labs-roadmap-title">{r.title}</h3>
                <p className="labs-roadmap-body">
                  {r.body}
                  {r.target ? (
                    <span
                      className="qt-mono"
                      style={{
                        display: "block",
                        marginTop: 8,
                        fontSize: 11,
                        letterSpacing: "0.08em",
                        color: "var(--qt-text-3, #7A8AA1)",
                      }}
                    >
                      Target: {r.target}
                    </span>
                  ) : null}
                </p>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────── ##
// Page shell.
// ─────────────────────────────────────────────────────────────────────── ##
export default function Labs() {
  usePageMeta(M.labs);
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);
  const [consult, setConsult] = useState({ open: false, source: "labs-hero" });
  const rootRef         = useReveal();

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/labs/landing`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "load failed"); });
    return () => { cancelled = true; };
  }, []);

  // Mega-menu's "Schedule a Call" item links to /labs?schedule=1 — auto
  // open the consult modal once the page mounts so the mega-menu CTA
  // works without a separate global event-bus.
  useEffect(() => {
    const sp = new URLSearchParams(window.location.search);
    const auto = sp.get("schedule") || sp.get("consult");
    if (auto) {
      const src = sp.get("source") || "labs-mega-menu";
      setConsult({ open: true, source: src });
    }
  }, []);

  const openConsult = (source) => setConsult({ open: true, source: source || "labs" });
  const closeConsult = () => setConsult((c) => ({ ...c, open: false }));

  // Guided tour state.  The "Skim the methodology" link inline with
  // the hero meta strip toggles this on; the floating <LabsTour>
  // overlay self-manages step + dismiss + ESC + arrow-keys.
  const [tourOpen, setTourOpen] = useState(false);
  const openTour  = () => setTourOpen(true);
  const closeTour = () => setTourOpen(false);

  const services = useMemo(() => data?.services || [], [data]);
  const cases    = useMemo(() => data?.case_studies || [], [data]);
  const creds    = useMemo(() => data?.credentials   || [], [data]);

  return (
    <div className="labs-root" data-testid="labs-page" ref={rootRef}>
      <SEOHead />
      <Header />
      <div className="labs-grid-overlay" aria-hidden="true" />
      <div className="labs-grain" aria-hidden="true" />

      <Hero data={data} onOpenConsult={openConsult} onStartTour={openTour} />

      {/* Iter269 — Labs headline reframe. PQC Audit Suite leads the page
          before Manifesto + the foundational services. Procurement + CISO
          traffic now hits the bundle within one viewport. */}
      <LabsSuiteHeroBand />

      <Manifesto />
      {services.length > 0 && <Services services={services} onOpenConsult={openConsult} />}
      <Methodology />
      {cases.length > 0 && <Engagements cases={cases} />}
      <TeamCredentials items={creds} />
      <TeamProfiles />
      <RoadmapTeaser />
      <HubFAQ />
      {services.length > 0 && <InquiryForm services={services} onOpenConsult={openConsult} />}

      <Footer />

      <ConsultationModal open={consult.open}
                         onClose={closeConsult}
                         source={consult.source} />

      <LabsTour open={tourOpen} onClose={closeTour} />

      {err && (
        <div className="labs-load-err" data-testid="labs-load-error">
          Could not load latest content
        </div>
      )}
    </div>
  );
}
